import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, MapPin } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { ContactForm } from "../components/ContactForm";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FaqSection } from "../sections/FaqSection";
import { useUI } from "../context/UIContext";
import { getCity, getService, fillBenefit, CITIES, SERVICE_LIST } from "../data/locations";

export default function LocationPage() {
  const { service: serviceSlug, city: citySlug } = useParams();
  const service = getService(serviceSlug);
  const city = getCity(citySlug);
  const { openUpload } = useUI();

  if (!service || !city) return <Navigate to="/standorte" replace />;

  const region = city.state === city.name ? "der Metropolregion" : city.state;
  const title = `${service.label} ${city.name}`;
  const path = `/${service.slug}/${city.slug}`;
  const desc = `${service.label} in ${city.name}: kostenlose, unabhängige Analyse Ihres ${service.kw}s. Persönlich, transparent & mit Wechselservice – Angebot in 24 Stunden.`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.label,
    areaServed: { "@type": "City", name: city.name },
    provider: { "@type": "ProfessionalService", name: "Energie-Tarifberater", areaServed: "DE" },
    name: title,
  };

  // internal linking: same service other cities (nearby by same state first)
  const sameState = CITIES.filter((c) => c.state === city.state && c.slug !== city.slug).slice(0, 6);
  const otherCities = CITIES.filter((c) => c.state !== city.state).slice(0, 8);
  const relatedCities = [...sameState, ...otherCities].slice(0, 8);

  return (
    <>
      <Seo title={title} description={desc} path={path} schema={schema} />
      <PageHero
        eyebrow={`${service.short} · ${city.state}`}
        titleLines={[service.label, `in <span class="font-serif italic text-blue-700">${city.name}</span>`]}
        subtitle={desc}
        breadcrumb={[{ label: "Standorte", to: "/standorte" }, { label: `${service.short} ${city.name}` }]}
      />

      <section className="bg-background py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-2xl sm:text-3xl font-heading font-semibold text-navy-900 leading-snug mb-10">
                {service.intro(city.name, region)}
              </p>
            </Reveal>

            <div className="grid sm:grid-cols-2 gap-6">
              {service.benefits.map((b, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <div className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-6 h-full hover:border-blue-300 transition-colors" data-testid={`loc-benefit-${i}`}>
                    <span className="shrink-0 inline-grid place-items-center h-10 w-10 rounded-xl bg-blue-50 text-blue-700">
                      <Check className="h-5 w-5" strokeWidth={2.5} />
                    </span>
                    <p className="text-navy-900 text-sm leading-relaxed pt-1.5">{fillBenefit(b, city.name)}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* other services in this city */}
            <Reveal>
              <div className="mt-12">
                <h2 className="text-xl font-heading text-navy-900 mb-4">Weitere Leistungen in {city.name}</h2>
                <div className="flex flex-wrap gap-3">
                  {SERVICE_LIST.filter((s) => s.slug !== service.slug).map((s) => (
                    <Link
                      key={s.slug}
                      to={`/${s.slug}/${city.slug}`}
                      className="rounded-full border border-slate-300 hover:border-blue-500 hover:text-blue-700 px-4 py-2 text-sm text-slate-700 transition-colors"
                      data-testid={`loc-service-${s.slug}`}
                    >
                      {s.label} {city.name}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* related cities */}
            <Reveal>
              <div className="mt-10">
                <h2 className="text-xl font-heading text-navy-900 mb-4">{service.label} in weiteren Städten</h2>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {relatedCities.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/${service.slug}/${c.slug}`}
                      className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-700 transition-colors"
                      data-testid={`loc-city-${c.slug}`}
                    >
                      <MapPin className="h-3.5 w-3.5" /> {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* sticky form */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:sticky lg:top-28 rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.15)]"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-2">Kostenloser {service.short}-Check · {city.name}</p>
              <h3 className="text-2xl font-heading font-semibold text-navy-900 mb-6">
                Angebot in <span className="font-serif italic text-blue-700">24 Stunden</span>
              </h3>
              <button
                onClick={openUpload}
                className="w-full group rounded-full bg-blue-700 hover:bg-blue-600 text-white py-4 font-medium transition-colors flex items-center justify-center gap-2 mb-5"
                data-testid="loc-upload-btn"
              >
                Rechnung hochladen
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <ContactForm compact topic={`${service.slug}-${city.slug}`} testid={`loc-form-${service.slug}-${city.slug}`} />
            </motion.div>
          </div>
        </div>
      </section>

      <FaqSection />
    </>
  );
}
